/**
 * ManufacturingWorkOrderCreateModal.jsx
 * =====================================
 *
 * Manufacturing Work Order Creation — dedicated, isolated chat modal.
 *
 * AgentModalRouter.jsx routes here when:
 * agent_key === "manufacturing-work-order".
 *
 * WIZARD:
 * Same wizard behavior as MaintenanceWorkOrderCreateModal /
 * MoveOrderCreateModal.
 *
 * The wizard is displayed:
 *   - Above the input in pre-chat mode.
 *   - On the right side in chat mode.
 *
 * Backend endpoint:
 *
 * POST /erp-agents/manufacturing-work-order/chat
 *
 * body:
 * {
 *   session_id,
 *   question,
 *   ui_selected_field?
 * }
 *
 * Expected backend response for full wizard synchronization:
 *
 * {
 *   "answer": "...",
 *   "ui_options": [...],
 *   "ui_option_type": "single_select",
 *   "ui_field": "OrganizationCode",
 *   "fields_collected": {
 *      "OrganizationCode": "BNR"
 *   },
 *   "missing_fields": [
 *      "ItemNumber"
 *   ],
 *   "order_status": "submitted",
 *   "work_order_number": "..."
 * }
 */

import React from "react";

import api from "@/lib/api";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  X,
  Gear,
  Sparkle,
  PaperPlaneRight,
  User as UserIcon,
  Robot,
  CheckCircle,
  XCircle,
} from "@phosphor-icons/react";

import { useErp } from "@/context/ErpContext";
import WizardFields from "@/components/erp/WizardFields";

// ─────────────────────────────────────────────────────────────────────────────
// Wizard field mappings
// ─────────────────────────────────────────────────────────────────────────────
//
// Keep the right-hand side aligned with the field names registered in:
//
// GET /erp-agents/agent-fields/manufacturing-work-order
//
// If your backend field names are already identical to WizardFields keys,
// CHAT_TO_WIZARD can remain empty.
//
// ─────────────────────────────────────────────────────────────────────────────

const CHAT_TO_WIZARD = {};

// Structured backend ui_field -> WizardFields field.
//
// These are the manufacturing fields commonly used by the work-order flow.
// Remove/change any field that does not exist in your WizardFields schema.

const OPTION_FIELD_TO_WIZARD_KEY = {
  OrganizationCode: "OrganizationCode",

  ItemNumber: "ItemNumber",

  CurrentItem: "ItemNumber",

  Quantity: "Quantity",

  WorkOrderTypeCode: "WorkOrderTypeCode",

  WorkOrderSubTypeCode: "WorkOrderSubTypeCode",

  WorkDefinitionName: "WorkDefinitionName",

  WorkDefinitionCode: "WorkDefinitionCode",

  WorkAreaCode: "WorkAreaCode",

  WorkCenterCode: "WorkCenterCode",

  StandardOperationCode: "StandardOperationCode",

  StandardOperationName: "StandardOperationName",

  PlannedStartDate: "PlannedStartDate",

  PlannedCompletionDate: "PlannedCompletionDate",

  Description: "Description",

  WorkOrderStatusCode: "WorkOrderStatusCode",
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function ts() {
  return new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Fallback option detection
// ─────────────────────────────────────────────────────────────────────────────

function extractListOptions(text) {
  if (!text) return null;

  const lines = text.split("\n");
  const matches = [];

  lines.forEach((line, idx) => {
    const m = line.trim().match(/^(?:[\*\-•]|\d+\.)\s+(.+)$/);

    if (m) {
      matches.push({
        idx,
        content: m[1].trim(),
      });
    }
  });

  if (matches.length < 2) {
    return null;
  }

  const options = matches.map(({ content }) => {
    const dashSplit = content.split(" - ");

    const firstPart = (dashSplit[0] || "").trim();

    const looksLikeCode =
      /^[A-Za-z0-9][A-Za-z0-9\-\/]*$/.test(firstPart);

    const value =
      looksLikeCode && dashSplit.length > 1
        ? firstPart
        : content;

    return {
      label: content,
      value,
    };
  });

  const matchedIdx = new Set(
    matches.map((m) => m.idx)
  );

  const strippedText = lines
    .filter((_, idx) => !matchedIdx.has(idx))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return {
    options,
    strippedText,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Yes / No detection
// ─────────────────────────────────────────────────────────────────────────────

function extractYesNo(text) {
  if (!text) return null;

  if (!/\(\s*yes\s*\/\s*no\s*\)/i.test(text)) {
    return null;
  }

  return [
    {
      label: "Yes",
      value: "Yes",
    },
    {
      label: "No",
      value: "No",
    },
  ];
}

// ─────────────────────────────────────────────────────────────────────────────
// Fallback UI derivation
// ─────────────────────────────────────────────────────────────────────────────

function deriveFieldUi(text) {
  const yesNo = extractYesNo(text);

  if (yesNo) {
    return {
      fieldUi: {
        field: "parsed_confirm",
        uiType: "confirm",
        options: yesNo,
      },
      displayText: text,
    };
  }

  const list = extractListOptions(text);

  if (list) {
    return {
      fieldUi: {
        field: "parsed_list",
        uiType: "radio",
        options: list.options,
      },
      displayText: list.strippedText || text,
    };
  }

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Markdown renderer
// ─────────────────────────────────────────────────────────────────────────────

function ChatMarkdown({ children }) {
  return (
    <div className="chat-md text-sm leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...props }) => (
            <p
              className="mb-2 last:mb-0 whitespace-pre-wrap"
              {...props}
            />
          ),

          strong: ({ node, ...props }) => (
            <strong
              className="font-bold text-gray-900"
              {...props}
            />
          ),

          ul: ({ node, ...props }) => (
            <ul
              className="list-disc pl-5 mb-2 space-y-0.5"
              {...props}
            />
          ),

          ol: ({ node, ...props }) => (
            <ol
              className="list-decimal pl-5 mb-2 space-y-0.5"
              {...props}
            />
          ),

          li: ({ node, ...props }) => (
            <li
              className="text-sm"
              {...props}
            />
          ),

          h3: ({ node, ...props }) => (
            <h3
              className="text-sm font-bold mb-1 mt-2 text-[#0020C2]"
              {...props}
            />
          ),

          code: ({ node, inline, ...props }) =>
            inline ? (
              <code
                className="font-mono text-[12px] bg-gray-100 px-1 py-0.5 rounded"
                {...props}
              />
            ) : (
              <code
                className="font-mono text-[12px] block bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto"
                {...props}
              />
            ),

          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-2">
              <table
                className="w-full text-xs border-collapse"
                {...props}
              />
            </div>
          ),

          thead: ({ node, ...props }) => (
            <thead
              className="bg-[#0020C2]/5"
              {...props}
            />
          ),

          th: ({ node, ...props }) => (
            <th
              className="border border-gray-200 px-2 py-1.5 text-left font-bold uppercase tracking-wider text-[10px] text-gray-600"
              {...props}
            />
          ),

          td: ({ node, ...props }) => (
            <td
              className="border border-gray-200 px-2 py-1.5 text-gray-800"
              {...props}
            />
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Option buttons
// ─────────────────────────────────────────────────────────────────────────────

function OptionButtons({ fieldUi, onPick }) {
  const options = fieldUi.options || [];

  if (!options.length) {
    return null;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Yes / No confirmation
  // ──────────────────────────────────────────────────────────────────────────

  if (fieldUi.uiType === "confirm") {
    return (
      <div
        className="mt-2 flex gap-2"
        data-testid={"chat-field-ui-" + fieldUi.field}
      >
        {options.map((opt) => {
          const isYes = /^y/i.test(opt.value);

          return (
            <button
              key={opt.value}
              onClick={() => onPick(opt)}
              className={
                "text-xs font-semibold px-4 py-1.5 rounded-full transition-colors " +
                (isYes
                  ? "bg-[#0020C2] text-white hover:bg-[#001AA3]"
                  : "border border-gray-300 text-gray-600 hover:bg-gray-50")
              }
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Action buttons
  // ──────────────────────────────────────────────────────────────────────────

  if (fieldUi.uiType === "action") {
    return (
      <div
        className="mt-2 flex flex-wrap gap-2"
        data-testid={"chat-field-ui-" + fieldUi.field}
      >
        {options.map((opt, index) => (
          <button
            key={opt.value}
            onClick={() => onPick(opt)}
            className={
              "text-xs font-semibold px-3.5 py-1.5 rounded-full transition-colors " +
              (index === 0
                ? "bg-[#0020C2]/10 text-[#0020C2] border border-[#0020C2]/30 hover:bg-[#0020C2]/20"
                : "bg-gray-900 text-white hover:bg-gray-800")
            }
          >
            {opt.label}
          </button>
        ))}
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Radio / single select
  // ──────────────────────────────────────────────────────────────────────────

  if (fieldUi.uiType === "radio") {
    return (
      <div
        className="mt-2 flex flex-col gap-1.5"
        data-testid={"chat-field-ui-" + fieldUi.field}
      >
        {options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onPick(opt)}
            className="flex items-start gap-2.5 text-left text-xs px-3 py-2 border border-gray-200 rounded-lg hover:border-[#0020C2] hover:bg-[#0020C2]/5 transition-colors group"
          >
            <span className="mt-0.5 shrink-0 w-3.5 h-3.5 rounded-full border-2 border-gray-300 group-hover:border-[#0020C2] transition-colors" />

            <span className="text-gray-800 leading-snug">
              {opt.label}
            </span>
          </button>
        ))}
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Pills / dropdown
  // ──────────────────────────────────────────────────────────────────────────

  return (
    <div
      className={
        fieldUi.uiType === "dropdown"
          ? "mt-2 flex flex-col gap-1"
          : "mt-2 flex flex-wrap gap-1.5"
      }
      data-testid={"chat-field-ui-" + fieldUi.field}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onPick(opt)}
          className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2] bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors text-left"
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Work Order status badge
// ─────────────────────────────────────────────────────────────────────────────

function OrderStatusBadge({
  status,
  workOrderNumber,
}) {
  if (status === "submitted") {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 w-fit">
        <CheckCircle
          size={13}
          weight="fill"
        />

        Work Order{" "}
        {workOrderNumber
          ? workOrderNumber + " "
          : ""}
        created
      </div>
    );
  }

  if (status === "cancelled") {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-3 py-1 w-fit">
        <XCircle
          size={13}
          weight="fill"
        />

        Work Order cancelled
      </div>
    );
  }

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Chat bubble
// ─────────────────────────────────────────────────────────────────────────────

function ChatBubble({
  msg,
  onOptionClick,
}) {
  const isUser =
    msg.role === "user";

  const {
    fieldUi,
    displayText,
  } = React.useMemo(() => {
    if (
      isUser ||
      msg.loading
    ) {
      return {
        fieldUi: null,
        displayText: msg.text,
      };
    }

    // ───────────────────────────────────────────────────────────────────────
    // Structured backend UI
    // ───────────────────────────────────────────────────────────────────────

    if (
      msg.result?.ui_options?.length
    ) {
      let uiType = "radio";

      if (
        msg.result.ui_option_type ===
        "yes_no"
      ) {
        uiType = "confirm";
      } else if (
        msg.result.ui_option_type ===
        "dropdown"
      ) {
        uiType = "dropdown";
      } else if (
        msg.result.ui_field ===
        "LineAction"
      ) {
        uiType = "action";
      }

      return {
        fieldUi: {
          field:
            msg.result.ui_field ||
            "ui_selection",

          uiType,

          options:
            msg.result.ui_options,
        },

        displayText: msg.text,
      };
    }

    // ───────────────────────────────────────────────────────────────────────
    // Plain-text fallback
    // ───────────────────────────────────────────────────────────────────────

    const derived =
      deriveFieldUi(msg.text);

    return (
      derived || {
        fieldUi: null,
        displayText: msg.text,
      }
    );
  }, [
    isUser,
    msg.loading,
    msg.result,
    msg.text,
  ]);

  return (
    <div
      className={
        "flex gap-2.5 " +
        (isUser
          ? "flex-row-reverse"
          : "flex-row")
      }
    >
      {/* Avatar */}
      <div
        className={
          "shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-white " +
          (isUser
            ? "bg-[#0020C2]"
            : "bg-gray-700")
        }
      >
        {isUser ? (
          <UserIcon
            size={11}
            weight="bold"
          />
        ) : (
          <Robot
            size={11}
            weight="bold"
          />
        )}
      </div>

      {/* Message bubble */}
      <div
        className={
          "max-w-[82%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed " +
          (isUser
            ? "bg-[#0020C2] text-white rounded-tr-sm"
            : "bg-white border border-gray-200 text-gray-900 rounded-tl-sm shadow-sm")
        }
      >
        {/* Loading */}
        {msg.loading && (
          <div className="flex items-center gap-1 py-0.5">
            {[0, 150, 300].map(
              (delay) => (
                <span
                  key={delay}
                  className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                  style={{
                    animationDelay:
                      delay + "ms",
                  }}
                />
              )
            )}
          </div>
        )}

        {/* Message text */}
        {!msg.loading &&
          displayText &&
          (isUser ? (
            <p className="whitespace-pre-wrap">
              {displayText}
            </p>
          ) : (
            <ChatMarkdown>
              {displayText}
            </ChatMarkdown>
          ))}

        {/* Structured options */}
        {fieldUi && (
          <OptionButtons
            fieldUi={fieldUi}
            onPick={(opt) =>
              onOptionClick?.(
                opt,
                fieldUi.field
              )
            }
          />
        )}

        {/* Work Order status */}
        {!msg.loading &&
          msg.result?.order_status && (
            <OrderStatusBadge
              status={
                msg.result
                  .order_status
              }
              workOrderNumber={
                msg.result
                  .work_order_number
              }
            />
          )}

        {/* Timestamp */}
        {msg.timestamp && (
          <div
            className={
              "text-[9px] mt-1 opacity-40 " +
              (isUser
                ? "text-right"
                : "text-left")
            }
          >
            {msg.timestamp}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Input bar
// ─────────────────────────────────────────────────────────────────────────────

function InputBar({
  inputRef,
  value,
  onChange,
  onKeyDown,
  onSend,
  busy,
  placeholder,
}) {
  return (
    <div className="border-t border-gray-200 px-4 py-3 bg-white shrink-0">
      <div className="flex items-end gap-2 border border-gray-300 bg-gray-50 focus-within:border-[#0020C2] focus-within:ring-2 focus-within:ring-blue-100">
        <textarea
          ref={inputRef}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          rows={1}
          disabled={busy}
          placeholder={placeholder}
          data-testid="agent-prompt"
          className="flex-1 bg-transparent px-3 py-2.5 text-sm resize-none outline-none placeholder-gray-400 disabled:opacity-50 leading-relaxed"
          style={{
            minHeight: "2.5rem",
            maxHeight: "7.5rem",
          }}
        />

        <button
          onClick={onSend}
          disabled={
            busy ||
            !value.trim()
          }
          className="m-2 w-8 h-8 bg-[#0020C2] text-white flex items-center justify-center hover:bg-[#001AA3] disabled:opacity-40 transition-colors shrink-0"
        >
          {busy ? (
            <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <PaperPlaneRight
              size={14}
              weight="fill"
            />
          )}
        </button>
      </div>

      <span className="text-[10px] text-gray-400 mt-1 block">
        ↵ send · shift+↵ new line
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Manufacturing Work Order Create Modal
// ─────────────────────────────────────────────────────────────────────────────

export default function ManufacturingWorkOrderCreateModal({
  onClose,
}) {
  const {
    openAgent,
    setOpenAgent,
    setSetupOpen,
    setPendingAgent,
    profile,
    agents,
  } = useErp();

  const agent = agents.find(
    (a) => a.key === openAgent
  );

  // ──────────────────────────────────────────────────────────────────────────
  // Session lifecycle
  // ──────────────────────────────────────────────────────────────────────────

  const [sessionId] =
    React.useState(
      () => crypto.randomUUID()
    );

  // ──────────────────────────────────────────────────────────────────────────
  // Chat state
  // ──────────────────────────────────────────────────────────────────────────

  const [messages, setMessages] =
    React.useState([]);

  const [input, setInput] =
    React.useState("");

  const [busy, setBusy] =
    React.useState(false);

  const [chatStarted, setChatStarted] =
    React.useState(false);

  // ──────────────────────────────────────────────────────────────────────────
  // Wizard state
  // ──────────────────────────────────────────────────────────────────────────

  const [fieldValues, setFieldValues] =
    React.useState({});

  const [
    missingFields,
    setMissingFields,
  ] = React.useState([]);

  const [
    fieldErrors,
  ] = React.useState({});

  const [
    fieldWarnings,
  ] = React.useState({});

  // ──────────────────────────────────────────────────────────────────────────
  // Refs
  // ──────────────────────────────────────────────────────────────────────────

  const messagesEndRef =
    React.useRef(null);

  const inputRef =
    React.useRef(null);

  // ──────────────────────────────────────────────────────────────────────────
  // Auto-scroll
  // ──────────────────────────────────────────────────────────────────────────

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView(
      {
        behavior: "smooth",
      }
    );
  }, [messages]);

  // ──────────────────────────────────────────────────────────────────────────
  // Close
  // ──────────────────────────────────────────────────────────────────────────

  const close = () => {
    setOpenAgent(null);
    onClose?.();
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Profile
  // ──────────────────────────────────────────────────────────────────────────

  const openProfile = () => {
    setPendingAgent(openAgent);

    close();

    setSetupOpen(true);
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Send message
  // ──────────────────────────────────────────────────────────────────────────
  //
  // uiSelectedField:
  //   Actual backend ui_field when the click came from structured UI.
  //
  // optionField:
  //   Same field, used for optimistic wizard synchronization.
  //
  // ──────────────────────────────────────────────────────────────────────────

  const sendMessage = async (
    text,
    uiSelectedField,
    optionField
  ) => {
    const userText =
      (text || "").trim();

    if (!userText || busy) {
      return;
    }

    // Clear input.
    setInput("");

    if (inputRef.current) {
      inputRef.current.style.height =
        "auto";
    }

    // Start chat mode.
    if (!chatStarted) {
      setChatStarted(true);
    }

    // ───────────────────────────────────────────────────────────────────────
    // Optimistic wizard fill
    // ───────────────────────────────────────────────────────────────────────

    if (
      optionField &&
      OPTION_FIELD_TO_WIZARD_KEY[
        optionField
      ]
    ) {
      const wizardKey =
        OPTION_FIELD_TO_WIZARD_KEY[
          optionField
        ];

      setFieldValues(
        (prev) => ({
          ...prev,
          [wizardKey]:
            userText,
        })
      );
    }

    // ───────────────────────────────────────────────────────────────────────
    // Add messages
    // ───────────────────────────────────────────────────────────────────────

    const now = ts();

    const userMessageId =
      Date.now();

    const loadingMessageId =
      userMessageId + 1;

    setMessages((prev) => [
      ...prev,

      {
        id: userMessageId,
        role: "user",
        text: userText,
        timestamp: now,
      },

      {
        id: loadingMessageId,
        role: "assistant",
        loading: true,
      },
    ]);

    setBusy(true);

    try {
      // ─────────────────────────────────────────────────────────────────────
      // Backend
      // ─────────────────────────────────────────────────────────────────────

      const res = await api.post(
        "/erp-agents/manufacturing-work-order/chat",
        {
          session_id:
            sessionId,

          question:
            userText,

          ...(uiSelectedField
            ? {
                ui_selected_field:
                  uiSelectedField,
              }
            : {}),
        }
      );

      const data =
        res.data;

      const reply =
        data?.answer ||
        data?.reply ||
        data?.message ||
        "Done.";

      // ─────────────────────────────────────────────────────────────────────
      // fields_collected -> wizard
      // ─────────────────────────────────────────────────────────────────────

      if (
        data?.fields_collected &&
        typeof data.fields_collected ===
          "object"
      ) {
        setFieldValues(
          (prev) => {
            const merged = {
              ...prev,
            };

            Object.entries(
              data.fields_collected
            ).forEach(
              ([key, value]) => {
                if (
                  value ===
                    null ||
                  value ===
                    undefined ||
                  value === ""
                ) {
                  return;
                }

                const wizardKey =
                  CHAT_TO_WIZARD[
                    key
                  ] ||
                  key;

                merged[
                  wizardKey
                ] = value;
              }
            );

            return merged;
          }
        );
      }

      // ─────────────────────────────────────────────────────────────────────
      // missing_fields -> wizard
      // ─────────────────────────────────────────────────────────────────────

      if (
        Array.isArray(
          data?.missing_fields
        )
      ) {
        setMissingFields(
          data.missing_fields
        );
      }

      // ─────────────────────────────────────────────────────────────────────
      // Replace loading bubble
      // ─────────────────────────────────────────────────────────────────────

      setMessages(
        (prev) =>
          prev.map(
            (message) =>
              message.id ===
              loadingMessageId
                ? {
                    ...message,
                    loading:
                      false,
                    text:
                      reply,
                    result:
                      data,
                    timestamp:
                      ts(),
                  }
                : message
          )
      );
    } catch (error) {
      // ─────────────────────────────────────────────────────────────────────
      // Error
      // ─────────────────────────────────────────────────────────────────────

      const errorMessage =
        error?.response
          ?.data?.detail ||
        error?.message ||
        "Something went wrong.";

      setMessages(
        (prev) =>
          prev.map(
            (message) =>
              message.id ===
              loadingMessageId
                ? {
                    ...message,
                    loading:
                      false,
                    text:
                      "Error: " +
                      errorMessage,
                    timestamp:
                      ts(),
                  }
                : message
          )
      );
    } finally {
      setBusy(false);

      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Input change
  // ──────────────────────────────────────────────────────────────────────────

  const handleInputChange = (
    e
  ) => {
    setInput(
      e.target.value
    );

    e.target.style.height =
      "auto";

    e.target.style.height =
      Math.min(
        e.target.scrollHeight,
        120
      ) + "px";
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Keyboard
  // ──────────────────────────────────────────────────────────────────────────

  const handleKeyDown = (
    e
  ) => {
    if (
      e.key === "Enter" &&
      !e.shiftKey
    ) {
      e.preventDefault();

      sendMessage(input);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`
        @keyframes chatFadeIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }

          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes wizardSlideIn {
          from {
            opacity: 0;
            transform: translateX(20px);
          }

          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .modal-pre {
          width: 672px;
          max-width: calc(100vw - 2rem);
          max-height: 90vh;
        }

        .modal-chat {
          width: min(92vw, 1400px);
          height: 90vh;
          max-width: 98vw;
          max-height: 96vh;
          min-width: 640px;
          min-height: 460px;
        }
      `}</style>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* Overlay */}
      {/* ─────────────────────────────────────────────────────────────────── */}

      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        {/* Backdrop intentionally has no onClick.
            Only X closes the modal. */}

        <div
          data-testid={
            "agent-modal-" +
            openAgent
          }
          className={
            "bg-white text-gray-900 border border-gray-300 flex flex-col overflow-hidden " +
            (chatStarted
              ? "modal-chat"
              : "modal-pre")
          }
          style={{
            transition: chatStarted
              ? "none"
              : "width 0.45s cubic-bezier(0.4,0,0.2,1), height 0.45s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          {/* ─────────────────────────────────────────────────────────────── */}
          {/* Header */}
          {/* ─────────────────────────────────────────────────────────────── */}

          <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">
                Oracle Fusion ERP · Vaku AI
              </div>

              <h3 className="text-2xl font-black tracking-tight mt-0.5">
                {agent?.label}
              </h3>

              <p className="text-xs text-gray-500 mt-0.5">
                {agent?.hint}
              </p>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              {/* Profile */}
              <button
                onClick={
                  openProfile
                }
                data-testid="agent-open-profile"
                className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1"
              >
                <Gear
                  size={13}
                  weight="bold"
                />

                {profile?.oracle_username ||
                  "Set up profile"}
              </button>

              {/* Close */}
              <button
                onClick={close}
                data-testid="agent-modal-close"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* ─────────────────────────────────────────────────────────────── */}
          {/* PRE-CHAT */}
          {/* ─────────────────────────────────────────────────────────────── */}

          {!chatStarted ? (
            <>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <WizardFields
                  agentKey={openAgent}
                  values={
                    fieldValues
                  }
                  onChange={
                    setFieldValues
                  }
                  missingFields={
                    missingFields
                  }
                  onCreateNew={() => {}}
                  fieldErrors={
                    fieldErrors
                  }
                  fieldWarnings={
                    fieldWarnings
                  }
                />
              </div>

              <InputBar
                inputRef={
                  inputRef
                }
                value={input}
                onChange={
                  handleInputChange
                }
                onKeyDown={
                  handleKeyDown
                }
                onSend={() =>
                  sendMessage(
                    input
                  )
                }
                busy={busy}
                placeholder={
                  agent?.hint ||
                  "Type a message…"
                }
              />
            </>
          ) : (
            /* ─────────────────────────────────────────────────────────────
               CHAT MODE
               60% chat / 40% wizard
               ───────────────────────────────────────────────────────────── */

            <div className="flex flex-1 overflow-hidden">
              {/* ───────────────────────────────────────────────────────── */}
              {/* LEFT — CHAT */}
              {/* ───────────────────────────────────────────────────────── */}

              <div
                className="flex flex-col overflow-hidden border-r border-gray-200"
                style={{
                  width: "60%",
                  animation:
                    "chatFadeIn 0.4s cubic-bezier(0.4,0,0.2,1)",
                }}
              >
                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
                  {messages.map(
                    (msg) => (
                      <ChatBubble
                        key={
                          msg.id
                        }
                        msg={msg}
                        onOptionClick={(
                          opt,
                          field
                        ) => {
                          const realField =
                            field &&
                            !field.startsWith(
                              "parsed_"
                            )
                              ? field
                              : undefined;

                          sendMessage(
                            opt.value,
                            realField,
                            realField
                          );
                        }}
                      />
                    )
                  )}

                  <div
                    ref={
                      messagesEndRef
                    }
                  />
                </div>

                {/* Input */}
                <InputBar
                  inputRef={
                    inputRef
                  }
                  value={input}
                  onChange={
                    handleInputChange
                  }
                  onKeyDown={
                    handleKeyDown
                  }
                  onSend={() =>
                    sendMessage(
                      input
                    )
                  }
                  busy={busy}
                  placeholder="Continue the conversation…"
                />
              </div>

              {/* ───────────────────────────────────────────────────────── */}
              {/* RIGHT — WIZARD */}
              {/* ───────────────────────────────────────────────────────── */}

              <div
                className="flex flex-col bg-white"
                style={{
                  width: "40%",
                  minWidth:
                    "360px",
                  animation:
                    "wizardSlideIn 0.45s cubic-bezier(0.4,0,0.2,1)",
                }}
              >
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Wizard heading */}
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
                    <Sparkle
                      size={11}
                      weight="fill"
                    />

                    Wizard fields

                    <span className="text-gray-400 normal-case font-normal tracking-normal ml-1">
                      · auto-filled
                      as you chat
                    </span>
                  </div>

                  {/* Wizard */}
                  <WizardFields
                    agentKey={
                      openAgent
                    }
                    values={
                      fieldValues
                    }
                    onChange={
                      setFieldValues
                    }
                    missingFields={
                      missingFields
                    }
                    onCreateNew={() => {}}
                    fieldErrors={
                      fieldErrors
                    }
                    fieldWarnings={
                      fieldWarnings
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}